import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRegister } from "@api/hooks";
import { showSuccess, showError, FormLayout, FormCard, FormField, Button } from "@common";
import { FORM_IDS, getDefaultRouteForUser, registerSchema } from "@utils";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";

export default function Register() {
  const nav = useNavigate();
  const registerMutation = useRegister();

  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(FORM_IDS.AUTH_REGISTER);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(registerSchema),
    mode: "onTouched",
    defaultValues: {
      email: "",
      first_name: "",
      last_name: "",
      password: "",
      password_confirm: "",
    },
  });

  async function onSubmit(values) {
    try {
      const data = await registerMutation.mutateAsync(values);

      recordSubmitSuccess();

      // Show success toast
      showSuccess(`Welcome, ${data.user.full_name}! Account created successfully.`);

      // Navigate based on role
      const dest = getDefaultRouteForUser(data.user);
      nav(dest, { replace: true });
    } catch (error) {
      console.error("Registration error:", error.response?.data);
      showError(getApiErrorMessage(error));
    }
  }

  return (
    <FormLayout>
      <FormCard title="Create Account" onSubmit={handleSubmit(onSubmit)} {...formTrackingProps}>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            label="First Name"
            name="first_name"
            type="text"
            placeholder="John"
            register={register}
            error={errors.first_name?.message}
          />

          <FormField
            label="Last Name"
            name="last_name"
            type="text"
            placeholder="Doe"
            register={register}
            error={errors.last_name?.message}
          />
        </div>

        <FormField
          label="Email"
          name="email"
          type="email"
          placeholder="john.doe@example.com"
          register={register}
          error={errors.email?.message}
        />

        <div>
          <FormField
            label="Password"
            name="password"
            type="password"
            placeholder="Create a strong password"
            register={register}
            error={errors.password?.message}
          />
          <div className="mt-1 text-xs text-smiles-lightGray">
            Must be 8+ characters with uppercase, lowercase, number, and special character
          </div>
        </div>

        <FormField
          label="Confirm Password"
          name="password_confirm"
          type="password"
          placeholder="Re-enter your password"
          register={register}
          error={errors.password_confirm?.message}
        />

        <Button as="button" type="submit" className="w-full" disabled={!isValid || isSubmitting}>
          {isSubmitting ? "Creating account…" : "Create Account"}
        </Button>

        <div className="mt-4 text-center">
          <span className="text-smiles-lightGray">Already have an account? </span>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => nav("/login")}
            className="text-smiles-blue font-medium hover:underline"
          >
            Sign In
          </Button>
        </div>
      </FormCard>
    </FormLayout>
  );
}
